import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Send, Plus, Minus, RefreshCw } from 'lucide-react';
import { useNotifications } from '../NotificationProvider';
import { EDTable, EDOrderItem, EDWaiter } from '../../types/EventDay';
import TagMultiSelect from '../ui/TagMultiSelect';
import { describeEventDayError, eventDayApi, type EventDayProblem } from '../../services/EventDayApiClient';
import { newKey } from '../../utils/newKey';
import { loadMenu, refreshMenuFromApi, MenuCategory } from '../../services/MenuService';
import { loadVenuesFromApi } from '../../services/VenueApiService';
import EmptyState from '../EmptyState';
import { WAITER_MENU_TABS_EVENT, WAITER_MENU_TAB_SET_EVENT, type WaiterMenuTabsState } from '../header/waiterMenuTabs';

interface PendingOrder {
  tableNumber: number;
  items: EDOrderItem[];
  notes: string;
}

interface WaiterViewProps {
  mode?: 'orders' | 'assignments';
  onModeChange?: (mode: 'orders' | 'assignments') => void;
  eventId?: string | null;
}

const WaiterView: React.FC<WaiterViewProps> = ({ mode = 'orders', eventId }) => {
  const { showSuccess, showError } = useNotifications();
  const [tables, setTables] = useState<EDTable[]>([]);
  const [waiters, setWaiters] = useState<EDWaiter[]>([]);
  const [waiterTableMap, setWaiterTableMap] = useState<Record<string, Set<string>>>({});
  const [assignSaving, setAssignSaving] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState<EDTable | null>(null);
  const [orderItems, setOrderItems] = useState<EDOrderItem[]>([]);
  const [noteText, setNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>([]);
  const [menuCategories, setMenuCategories] = useState<MenuCategory[]>([]);
  const [activeTab, setActiveTab] = useState<string>('');
  const [tablePage, setTablePage] = useState(0);
  const [fpZoneNames, setFpZoneNames] = useState<string[]>([]);
  // loading | ready | a plain-words problem. A failed load never looks like an empty room.
  const [loadState, setLoadState] = useState<'loading' | 'ready'>('loading');
  const [problem, setProblem] = useState<EventDayProblem | null>(null);
  // One key per order attempt: tapping Send twice, or retrying after a dropped connection, never places the order twice.
  const attemptKey = useRef(newKey());

  const loadAssignments = useCallback(async () => {
    try {
      const [tData, wData] = await Promise.all([
        eventDayApi.getTables(),
        eventDayApi.getWaiters(),
      ]);
      const loadedTables: EDTable[] = tData.tables || [];
      const loadedWaiters: EDWaiter[] = wData.waiters || [];
      setTables(loadedTables);
      setWaiters(loadedWaiters);
      const map: Record<string, Set<string>> = {};
      await Promise.all(loadedWaiters.map(async w => {
        try {
          const r = await eventDayApi.getWaiterTables(w.id);
          map[w.id] = new Set((r.tables || []).map((t: EDTable) => t.id));
        } catch { map[w.id] = new Set(); }
      }));
      setWaiterTableMap(map);
      setProblem(null);
    } catch (err) {
      setProblem(describeEventDayError(err));
    } finally {
      setLoadState('ready');
    }
  }, [eventId]);

  useEffect(() => {
    void loadVenuesFromApi().then(venues => {
      const names: string[] = [];
      for (const v of venues) {
        if (v.archived) continue;
        for (const fp of v.floorPlans) {
          for (const room of fp.rooms) {
            if (room.type !== 'table' && room.name) names.push(room.name);
          }
        }
      }
      setFpZoneNames(Array.from(new Set(names)));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode === 'assignments') { loadAssignments(); return; }
    setLoadState('loading');
    eventDayApi.getTables(eventId ?? undefined)
      .then(d => { setTables(d.tables || []); setProblem(null); })
      .catch(err => setProblem(describeEventDayError(err)))
      .finally(() => setLoadState('ready'));

    const seed = loadMenu();
    setMenuCategories(seed);
    if (seed.length > 0) setActiveTab(seed[0].tab);

    refreshMenuFromApi()
      .then(cats => {
        setMenuCategories(cats);
        if (cats.length > 0) setActiveTab(t => t || cats[0].tab);
      })
      .catch(() => {});
  }, [mode, loadAssignments, eventId]);

  const allSections = Array.from(new Set([
    ...fpZoneNames,
    ...tables.map(t => t.section).filter(Boolean) as string[],
  ]));

  const handleAssign = async (waiterId: string, selectedTableIds: string[], selectedSectionNames: string[]) => {
    const current = waiterTableMap[waiterId] ?? new Set<string>();
    // Tables from selected sections
    const sectionTableIds = tables.filter(t => t.section && selectedSectionNames.includes(t.section)).map(t => t.id);
    // Union: explicitly selected tables + all tables in selected sections
    const next = new Set([...selectedTableIds, ...sectionTableIds]);
    // Enforce max 10 tables
    const limited = new Set(Array.from(next).slice(0, 10));
    setWaiterTableMap(prev => ({ ...prev, [waiterId]: limited }));
    setAssignSaving(waiterId);
    try {
      await eventDayApi.assignWaiterTables(waiterId, Array.from(limited));
    } catch (err) {
      setWaiterTableMap(prev => ({ ...prev, [waiterId]: current }));
      showError(describeEventDayError(err).message);
    } finally {
      setAssignSaving(null);
    }
  };

  const tabs = Array.from(new Set(menuCategories.map(c => c.tab)));
  const visibleCategories = menuCategories.filter(c => c.tab === activeTab);

  // The category picker lives in the global bar: report the categories up and
  // take the choice back.
  const tabsKey = tabs.join('|');
  useEffect(() => {
    const detail: WaiterMenuTabsState | null = tabs.length > 1 ? { tabs, active: activeTab } : null;
    window.dispatchEvent(new CustomEvent(WAITER_MENU_TABS_EVENT, { detail }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsKey, activeTab]);
  useEffect(() => {
    const onSet = (e: Event) => setActiveTab((e as CustomEvent<string>).detail);
    window.addEventListener(WAITER_MENU_TAB_SET_EVENT, onSet);
    return () => {
      window.removeEventListener(WAITER_MENU_TAB_SET_EVENT, onSet);
      window.dispatchEvent(new CustomEvent(WAITER_MENU_TABS_EVENT, { detail: null }));
    };
  }, []);

  const reloadTables = () => {
    setLoadState('loading');
    eventDayApi.getTables(eventId ?? undefined)
      .then(d => { setTables(d.tables || []); setProblem(null); })
      .catch(err => setProblem(describeEventDayError(err)))
      .finally(() => setLoadState('ready'));
  };

  const TABLE_PAGE_SIZE = 8;
  const tablePageCount = Math.ceil(tables.length / TABLE_PAGE_SIZE);
  const visibleTables = tables.slice(tablePage * TABLE_PAGE_SIZE, (tablePage + 1) * TABLE_PAGE_SIZE);

  const addItem = (name: string) => {
    const existing = orderItems.find(i => i.name === name);
    if (existing) {
      setOrderItems(orderItems.map(i =>
        i.name === name ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setOrderItems([...orderItems, { name, quantity: 1 }]);
    }
  };

  const removeItem = (name: string) => {
    setOrderItems(orderItems.filter(i => i.name !== name));
  };

  const adjustQty = (name: string, delta: number) => {
    setOrderItems(orderItems
      .map(i => i.name === name ? { ...i, quantity: i.quantity + delta } : i)
      .filter(i => i.quantity > 0)
    );
  };

  const getQty = (name: string) => orderItems.find(i => i.name === name)?.quantity ?? 0;

  const BAR_TITLE_KEYWORDS = ['drinks', 'signature drinks', 'bar selections', 'cocktails', 'bar menu', 'beverages', 'mocktails'];

  const submitOrder = async () => {
    if (!selectedTable || orderItems.length === 0) return;
    setSubmitting(true);
    try {
      // Build set of bar item names — use explicit vendor field, fall back to title keyword match
      const barItemNames = new Set<string>();
      menuCategories.forEach(cat => {
        const isBar = cat.vendor === 'bar' || BAR_TITLE_KEYWORDS.includes(cat.title.toLowerCase());
        if (isBar) cat.items.forEach(item => barItemNames.add(item.name));
      });

      const foodItems = orderItems.filter(i => !barItemNames.has(i.name));
      const barItems = orderItems.filter(i => barItemNames.has(i.name));

      const key = attemptKey.current;
      await Promise.all([
        foodItems.length > 0 && eventDayApi.submitOrder(selectedTable.id, null, foodItems, noteText, 'food', `${key}-food`),
        barItems.length > 0 && eventDayApi.submitOrder(selectedTable.id, null, barItems, noteText, 'bar', `${key}-bar`),
      ]);
      attemptKey.current = newKey();

      setPendingOrders(prev => [...prev, { tableNumber: selectedTable.number, items: orderItems, notes: noteText }]);
      setOrderItems([]);
      setNoteText('');
      setSelectedTable(null);
      showSuccess('Order submitted successfully!');
    } catch (err) { showError(describeEventDayError(err).message); } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ed-waiter-body">
      {/* Assignments view */}
      {mode === 'assignments' && (
        <div className="ed-waiter-assignments">
          {loadState === 'loading' ? (
            <p className="ed-panel-empty" role="status"><RefreshCw size={16} className="ed-spin" aria-hidden /> Loading staff and tables…</p>
          ) : problem ? (
            <div className="ed-error-state" role="alert">
              <p>{problem.message}</p>
              <button className="ed-btn-secondary" onClick={loadAssignments}>Try again</button>
            </div>
          ) : waiters.length === 0 ? (
            <EmptyState compact title="No staff yet" description="Add staff in Settings → Event Day." />
          ) : waiters.map(w => {
            const assigned = waiterTableMap[w.id] ?? new Set<string>();
            const assignedTableIds = Array.from(assigned);
            const assignedTables = tables.filter(t => assigned.has(t.id));
            const assignedSections = Array.from(new Set(assignedTables.map(t => t.section).filter(Boolean))) as string[];

            return (
              <div key={w.id} className="ed-waiter-assign-card">
                <div className="ed-waiter-assign-header">
                  <div className="ed-waiter-assign-name">
                    <span className="ed-waiter-assign-avatar">{w.name.charAt(0).toUpperCase()}</span>
                    <div>
                      <span>{w.name}</span>
                      {w.role && w.role !== 'waiter' && <span className="ed-waiter-role-badge">{w.role}</span>}
                      <div className="ed-waiter-assign-summary">{assigned.size} table{assigned.size !== 1 ? 's' : ''} assigned</div>
                    </div>
                  </div>
                  {assignSaving === w.id && <span className="ed-waiter-saving-hint">saving…</span>}
                </div>

                {tables.length === 0 ? (
                  <p className="ed-waiter-empty-hint">Add tables first in Settings.</p>
                ) : (
                  <div className="ed-waiter-assign-selects">
                    {allSections.length > 0 && (
                      <div className="ed-assign-field">
                        <label className="ed-assign-label">Sections <span className="ed-assign-cap">(up to 5)</span></label>
                        <TagMultiSelect
                          values={assignedSections}
                          options={allSections}
                          allowCreate={false}
                          placeholder="Assign sections…"
                          maxVisible={3}
                          onChange={selected => handleAssign(w.id, assignedTableIds, selected.slice(0, 5))}
                        />
                      </div>
                    )}
                    <div className="ed-assign-field">
                      <label className="ed-assign-label">Tables <span className="ed-assign-cap">(up to 10)</span></label>
                      <TagMultiSelect
                        maxVisible={3}
                        values={assignedTableIds.map(id => {
                          const t = tables.find(x => x.id === id);
                          return t ? `T${t.number}${t.name ? ` ${t.name}` : ''}${t.section ? ` · ${t.section}` : ''}` : '';
                        }).filter(Boolean)}
                        options={tables.map(t => `T${t.number}${t.name ? ` ${t.name}` : ''}${t.section ? ` · ${t.section}` : ''}`)}
                        allowCreate={false}
                        placeholder="Assign tables…"
                        onChange={selected => {
                          const ids = selected.slice(0, 10).map(label => {
                            const t = tables.find(x => `T${x.number}${x.name ? ` ${x.name}` : ''}${x.section ? ` · ${x.section}` : ''}` === label);
                            return t?.id;
                          }).filter((id): id is string => id !== undefined);
                          handleAssign(w.id, ids, assignedSections);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Orders view */}
      {mode === 'orders' && <>
        {/* Column 1 — table picker */}
        <div className="ed-waiter-tables-col">
          <div className="ed-section-label">Tables</div>
          {loadState === 'loading'
            ? <p className="ed-panel-empty" role="status"><RefreshCw size={16} className="ed-spin" aria-hidden /> Loading tables…</p>
            : problem
            ? (
              <div className="ed-error-state" role="alert">
                <p>{problem.message}</p>
                <button className="ed-btn-secondary" onClick={() => reloadTables()}>Try again</button>
              </div>
            )
            : tables.length === 0
            ? <p className="ed-panel-empty">No tables configured yet. Add them in Event Day setup.</p>
            : (
              <>
                <div className="ed-table-chips ed-table-chips-vertical">
                  {visibleTables.map(t => (
                    <button
                      key={t.id}
                      className={`ed-table-chip ${selectedTable?.id === t.id ? 'active' : ''}`}
                      onClick={() => setSelectedTable(selectedTable?.id === t.id ? null : t)}
                    >
                      T{t.number}{t.name ? ` — ${t.name}` : ''}
                    </button>
                  ))}
                </div>
                {tablePageCount > 1 && (
                  <div className="ed-table-pagination">
                    <button
                      className="ed-table-page-btn"
                      onClick={() => setTablePage(p => Math.max(0, p - 1))}
                      disabled={tablePage === 0}
                    >‹</button>
                    <span className="ed-table-page-label">{tablePage + 1} / {tablePageCount}</span>
                    <button
                      className="ed-table-page-btn"
                      onClick={() => setTablePage(p => Math.min(tablePageCount - 1, p + 1))}
                      disabled={tablePage === tablePageCount - 1}
                    >›</button>
                  </div>
                )}
              </>
            )
          }

          {pendingOrders.length > 0 && (
            <div className="ed-pending-log ed-pending-log--push-bottom">
              <div className="ed-section-label">Submitted</div>
              {pendingOrders.slice().reverse().map((o, i) => (
                <div key={i} className="ed-pending-item">
                  <span className="ed-pending-table">T{o.tableNumber}</span>
                  <span className="ed-pending-items">{o.items.map(it => `${it.quantity}× ${it.name}`).join(', ')}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Column 2 — current order / cart */}
        <div className="ed-waiter-cart-col">
          {!selectedTable ? (
            <p className="ed-panel-empty">Select a table to start an order.</p>
          ) : orderItems.length === 0 ? (
            <p className="ed-panel-empty">No items added yet — pick from the menu.</p>
          ) : (
            <div className="ed-current-order">
              {orderItems.map(item => (
                <div key={item.name} className="ed-order-line">
                  <span className="ed-order-line-name">{item.name}</span>
                  <div className="ed-qty-controls">
                    <button className="ed-qty-btn" onClick={() => adjustQty(item.name, -1)}><Minus size={12} /></button>
                    <span className="ed-qty-val">{item.quantity}</span>
                    <button className="ed-qty-btn" onClick={() => adjustQty(item.name, 1)}><Plus size={12} /></button>
                  </div>
                  <button className="ed-btn-remove" onClick={() => removeItem(item.name)}>×</button>
                  <button
                    className="ed-btn-submit ed-btn-submit-inline"
                    onClick={submitOrder}
                    disabled={submitting}
                    title={`Submit Order — T${selectedTable.number}`}
                  >
                    <Send size={13} />
                  </button>
                </div>
              ))}
              <textarea
                className="ed-notes-input"
                placeholder="Notes (optional)…"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                rows={2}
              />
            </div>
          )}
        </div>

        {/* Column 3 — menu sidebar */}
        <div className="ed-menu-sidebar">
          <div className="ed-menu-header">
            <div className="ed-section-label">Menu</div>
          </div>

          <div className="ed-menu-categories">
            {visibleCategories.map(cat => (
              <div key={cat.title} className="ed-menu-category">
                <div className="ed-menu-cat-title">{cat.title}</div>
                {cat.items.map(item => {
                  const qty = getQty(item.name);
                  return (
                    <div key={item.name} className="ed-menu-item">
                      <div className="ed-menu-item-info">
                        <span className="ed-menu-item-name">{item.name}</span>
                        {item.desc && <span className="ed-menu-item-desc">{item.desc}</span>}
                      </div>
                      {qty > 0 ? (
                        <div className="ed-qty-controls">
                          <button className="ed-qty-btn" onClick={() => adjustQty(item.name, -1)}><Minus size={12} /></button>
                          <span className="ed-qty-val">{qty}</span>
                          <button className="ed-qty-btn" onClick={() => adjustQty(item.name, 1)}><Plus size={12} /></button>
                        </div>
                      ) : (
                        <button
                          className="ed-menu-add-btn"
                          onClick={() => addItem(item.name)}
                          disabled={!selectedTable}
                          title={!selectedTable ? 'Select a table first' : 'Add to order'}
                        >
                          <Plus size={13} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </>}
    </div>
  );
};

export default WaiterView;
